import { useEffect, useState } from "react";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SearchableSelect from "@/components/SearchableSelect";
import { GripVertical, X } from "lucide-react";

function ListRow({ item, isDragging, onDragStartRow, onDropRow, onRemove, testPrefix }) {
  return (
    <div data-testid={`${testPrefix}-row-${item}`}
      draggable
      onDragStart={onDragStartRow}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDropRow}
      className={`flex items-center gap-2 px-2 py-1.5 border-b last:border-0 border-slate-100 text-sm cursor-grab active:cursor-grabbing select-none transition-colors ${isDragging ? "bg-blue-50" : "bg-white hover:bg-slate-50"}`}>
      <GripVertical className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      <span className="flex-1 truncate">{item}</span>
      <button data-testid={`${testPrefix}-remove-${item}`}
        className="text-slate-400 hover:text-red-700 transition-colors" onClick={onRemove}>
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function DraggableList({ title, hint, items, onChange, options, testPrefix }) {
  const [dragIdx, setDragIdx] = useState(null);

  const move = (to, fromOverride) => {
    const from = fromOverride ?? dragIdx;
    if (from === null || from === to) return;
    const next = [...items];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    onChange(next);
    setDragIdx(null);
  };

  const available = options.filter((o) => !items.some((i) => i.toLowerCase() === o.toLowerCase()));

  const add = (val) => {
    if (!val || items.some((i) => i.toLowerCase() === val.toLowerCase())) return;
    onChange([...items, val]);
  };

  return (
    <div className="border border-slate-300 rounded-sm flex flex-col">
      <div className="bg-slate-100 px-3 py-2 border-b border-slate-300">
        <div className="text-sm font-bold uppercase tracking-tight">{title} ({items.length})</div>
        <div className="text-[11px] text-slate-500">{hint}</div>
      </div>
      <div className="max-h-72 overflow-y-auto flex-1">
        {items.map((it, idx) => (
          <ListRow key={it} item={it} isDragging={dragIdx === idx} testPrefix={testPrefix}
            onDragStartRow={(e) => { setDragIdx(idx); e.dataTransfer.setData("text/row-index", String(idx)); }}
            onDropRow={(e) => { const from = parseInt(e.dataTransfer.getData("text/row-index"), 10); move(idx, Number.isNaN(from) ? null : from); }}
            onRemove={() => onChange(items.filter((x) => x !== it))} />
        ))}
        {items.length === 0 && <p className="text-xs text-slate-400 italic px-3 py-2">Lista vuota</p>}
      </div>
      <div className="p-2 border-t border-slate-200">
        <SearchableSelect options={available} onSelect={add} placeholder="Cerca in anagrafica..." testId={`${testPrefix}-add`} />
      </div>
      {options.length === 0 && (
        <p className="text-[11px] text-amber-700 bg-amber-50 px-3 py-1.5 border-t border-amber-200">
          Anagrafica vuota: carica prima i dati con "Import XLSM" o "Anagrafiche".
        </p>
      )}
    </div>
  );
}

export default function BaseListsDialog({ open, onClose, dayNum, personnel, sites, subcontractors, onSave }) {
  const [pers, setPers] = useState([]);
  const [cant, setCant] = useState([]);
  const [subs, setSubs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [master, setMaster] = useState({ personnel: [], sites: [], subcontractors: [] });

  useEffect(() => {
    if (open) {
      setPers(personnel || []);
      setCant(sites || []);
      setSubs(subcontractors || []);
      api.get("/master").then((r) => setMaster(r.data)).catch(() => {});
    }
    // i valori iniziali vengono caricati solo all'apertura del dialog
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await onSave(pers, cant, subs);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white rounded-sm max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="base-lists-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-lg font-bold tracking-tight">Liste base del giorno {String(dayNum).padStart(2, "0")}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500">
          Trascina le righe per riordinarle. I cantieri aggiunti compaiono subito come card nel giorno; quelli rimossi restano come card (eliminabili singolarmente).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DraggableList title="Personale" hint="Devono risultare tutti assegnati" items={pers} onChange={setPers} options={master.personnel} testPrefix="base-personnel" />
          <DraggableList title="Cantieri" hint="Cantieri previsti per il giorno" items={cant} onChange={setCant} options={master.sites} testPrefix="base-sites" />
          <DraggableList title="Subappaltatori" hint="Aziende disponibili nel giorno" items={subs} onChange={setSubs} options={master.subcontractors} testPrefix="base-subs" />
        </div>
        <Button data-testid="base-lists-save" onClick={save} disabled={saving}
          className="rounded-sm bg-slate-900 hover:bg-slate-700 text-white">
          {saving ? "Salvataggio..." : "Salva liste"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
